import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  IconButton,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { masqueradeService, MasqueradeUser } from '../services/masqueradeService';
import { firmService } from '../services/firmService';
import MasqueradeTable from '../components/MasqueradeTable';
import MasqueradeFilters from '../components/MasqueradeFilters';
import MasqueradePagination from '../components/MasqueradePagination';
import MasqueradeHeader from '../components/MasqueradeHeader';
import { useNavigate } from 'react-router-dom';

interface Firm {
  id: number;
  name: string;
  type: string;
}

const MasqueradeUsers: React.FC = () => {
  const [users, setUsers] = useState<MasqueradeUser[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUser, setLoadingUser] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedFirm, setSelectedFirm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');

  const navigate = useNavigate();

  const roles = [
    { value: '', label: 'All Roles' },
    { value: 'client_admin', label: 'Client Admin' },
    { value: 'client_user', label: 'Client User' },
    { value: 'vendor_user', label: 'Vendor User' },
    { value: 'vendor_staff', label: 'Vendor Staff' },
    { value: 'medical_user', label: 'Medical User' },
    { value: 'internal_staff', label: 'Internal Staff' },
  ];

  useEffect(() => {
    fetchUsers();
    fetchFirms();
  }, [searchTerm, selectedRole, selectedFirm, currentPage]);

  // Handle redirect after masquerade refresh
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('masqueradeRedirect');
    if (redirectPath) {
      sessionStorage.removeItem('masqueradeRedirect');
      navigate(redirectPath);
    }
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage };
      
      if (searchTerm) params.search = searchTerm;
      if (selectedRole) params.role = selectedRole;
      if (selectedFirm) params.firm_id = parseInt(selectedFirm);

      const response = await masqueradeService.getUsersList(params);
      if (response.success) {
        setUsers(response.data.data);
        setTotalPages(response.data.last_page);
        setTotalUsers(response.data.total);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to fetch users');
      showSnackbar('Failed to fetch users. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await firmService.getAllFirms();
      if (response.success) {
        setFirms(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch firms:', error);
    }
  };

  const handleMasquerade = async (user: MasqueradeUser) => {
    if (!window.confirm(`Are you sure you want to masquerade as ${user.name}?`)) {
      return;
    }

    try {
      setLoadingUser(user.id);
      const response = await masqueradeService.startMasquerade(user.id);
      
      if (response.success && response.data?.token) {
        // Update the token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Show success message
        showSnackbar(`Successfully masquerading as ${user.name}`, 'success');
        
        // Redirect to dashboard and refresh after a short delay
        setTimeout(() => {
          // Store the redirect path in sessionStorage before refresh
          sessionStorage.setItem('masqueradeRedirect', '/');
          window.location.reload();
        }, 1000);
      } else {
        setError(response.message || 'Failed to start masquerade');
        showSnackbar(response.message || 'Failed to start masquerade', 'error');
      }
    } catch (error: any) {
      console.error('Masquerade failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to start masquerade';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingUser(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedFirm('');
    setCurrentPage(1);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <MasqueradeHeader
          totalUsers={totalUsers}
          currentFilters={{
            search: searchTerm,
            role: selectedRole,
            firm: selectedFirm
          }}
        />

        {/* Security Warning */}
        <Box sx={{ p: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SecurityIcon color="warning" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Security Warning:
              </Typography>
              <Typography variant="body2">
                When masquerading, you will have full access to the selected user's account. 
                All actions will be performed as that user. Use this feature responsibly and only for legitimate administrative purposes.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <MasqueradeFilters
          searchTerm={searchTerm}
          selectedRole={selectedRole}
          selectedFirm={selectedFirm}
          firms={firms}
          roles={roles}
          onSearchChange={setSearchTerm}
          onRoleChange={setSelectedRole}
          onFirmChange={setSelectedFirm}
          onSearch={handleSearch}
          onClearFilters={clearFilters}
        />

        {/* Error Message */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert 
              severity="error" 
              onClose={handleCloseError}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleCloseError}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Users List */}
        {!loading && (
          <MasqueradeTable
            users={users}
            loading={loading}
            loadingUserId={loadingUser}
            onMasquerade={handleMasquerade}
          />
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <MasqueradePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MasqueradeUsers;
